import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { productSearchApi, StoreResponse, StoreResponses } from '@/stores/api';
import { BrowseRequestDto, GroupedProductsDto, PageRequestDto } from '@/api';
import { GroupedProducts, SearchUtility } from '@/stores/search/util';
import ErrorKey from '@/util/ErrorKey';
import * as _ from 'lodash';

@Module({
  namespaced: true,
  name: 'BrowseStore',
})
export default class BrowseStore extends VuexModule {
  private loading = true;
  private loadingNewPage = false;
  private readonly searchResult: GroupedProducts = _.cloneDeep(SearchUtility.EmptyBrowseResult);
  private searchRequest: BrowseRequestDto = _.cloneDeep(SearchUtility.EmptyBrowseRequest);
  private flatProductsCount = 0;
  private distinctProductCount = 0;

  get currentSearchRequest(): BrowseRequestDto {
    return this.searchRequest;
  }

  get currentSearchResult(): GroupedProducts {
    return this.searchResult;
  }

  get currentDistinctProductCount(): number {
    return this.distinctProductCount;
  }

  @Action
  public async updateSearchRequest(searchRequest: BrowseRequestDto): Promise<StoreResponse<GroupedProductsDto>> {
    this.context.commit('setNewSearchRequest', searchRequest);
    return this.fetchProducts();
  }

  @Action
  public async updateCategory(categoryId: string): Promise<StoreResponse<GroupedProductsDto>> {
    const browseRequest: BrowseRequestDto = this.currentSearchRequest;
    if (!browseRequest.filter) {
      browseRequest.filter = {};
    }
    browseRequest.filter.categoryId = categoryId;
    this.context.commit('setNewSearchRequest', browseRequest);
    return this.fetchProducts();
  }

  @Action
  public async updateSorting(
    sort: { fieldName?: string, direction?: string },
  ): Promise<StoreResponse<GroupedProductsDto>> {
    const browseRequest: BrowseRequestDto = this.currentSearchRequest;
    if (!browseRequest.page) {
      browseRequest.page = {};
    }
    browseRequest.page.sortBy = sort.fieldName;
    browseRequest.page.sortDirection = sort.direction;
    this.context.commit('setNewSearchRequest', browseRequest);
    return this.fetchProducts();
  }

  get isLoadingNewPage(): boolean {
    return this.loadingNewPage;
  }

  get currentProductCount(): number {
    return this.flatProductsCount;
  }

  @Action
  public async fetchAdditionalPage(pageRequest: PageRequestDto): Promise<StoreResponse<GroupedProductsDto>> {
    this.setLoadingNewPage(true);
    try {
      this.context.commit('setSearchRequestPage', pageRequest);
      const response = await productSearchApi.browseCategoryPage('GRM', this.searchRequest);
      this.context.commit('mergeSearchResults', response.data);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.ProductListFetchAction);
    } finally {
      this.setLoadingNewPage(false);
    }
  }

  @Mutation
  private setNewSearchRequest(searchRequest: BrowseRequestDto) {
    if (searchRequest.page) {
      searchRequest.page.page = 0;
    }
    this.searchRequest = searchRequest;
  }

  @Mutation
  private setSearchRequestPage(newPage: PageRequestDto) {
    this.searchRequest.page = newPage;
  }


  @Action
  public async fetchProducts(): Promise<StoreResponse<GroupedProductsDto>> {
    this.setLoading(true);
    try {
      const response = await productSearchApi.browseCategoryPage('GRM', this.searchRequest);
      this.context.commit('setSearchResult', response.data);
      return StoreResponses.apiSuccess(response);
    } catch (e) {
      return StoreResponses.error(e, ErrorKey.ProductListFetchAction);
    } finally {
      this.setLoading(false);
    }
  }

  @Mutation
  private setSearchResult(searchResult: GroupedProductsDto) {
    this.searchResult.facets = searchResult.facets;
    this.searchResult.filter = searchResult.filter;
    this.searchResult.page = searchResult.page;
    this.searchResult.queryDurationMs = searchResult.queryDurationMs;
    this.searchResult.groupings = SearchUtility.splitGroupingsByUnit(searchResult.groupings, searchResult.filter);
    this.flatProductsCount = SearchUtility.countProductsIn(this.searchResult.groupings);
    this.distinctProductCount = SearchUtility.countDistinctProductsIn(this.searchResult.groupings);
  }

  get isLoading(): boolean {
    return this.loading;
  }

  @Mutation
  private setLoading(loading: boolean) {
    this.loading = loading;
  }

  @Mutation
  private mergeSearchResults(searchResult: GroupedProductsDto) {
    this.searchResult.page = searchResult.page;
    this.searchResult.groupings = SearchUtility.mergeGroupings(
      this.searchResult.groupings, searchResult.groupings, this.searchResult.filter,
    );
    this.flatProductsCount = SearchUtility.countProductsIn(this.searchResult.groupings);
    this.distinctProductCount = SearchUtility.countDistinctProductsIn(this.searchResult.groupings);
  }

  @Mutation
  private setLoadingNewPage(loading: boolean) {
    this.loadingNewPage = loading;
  }
}
